import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  Paper,
  Select,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  SvgIcon,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Add, Delete, ExpandMore } from '@mui/icons-material';
import { FC, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import {
  MarketItem,
  MarketItemUom,
  Instance,
  Market,
  MarketCatalogue,
  MarketOrderType,
  MarketCategory,
  UomType,
  client,
  MarketSupplier,
} from '../../app/services/api/orderManagementClient';
import StoreLevelMOQGrid from './StoreLevelMOQGrid';
import useLogError from '../../hooks/useLogError';
import dayjs, { Dayjs } from 'dayjs';
import { UserContext } from '../../components/shared/useUser';

const toDateValue = (value?: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const ProductForm: FC = () => {
  const { t } = useTranslation('productManagement');
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logError, extractErrorMessage } = useLogError();
  const { selectedStore } = useContext(UserContext);

  const isEditMode = Boolean(id);
  const companyId = searchParams.get('companyId');
  const customerTypeId = searchParams.get('customerTypeId');
  const searchTerm = searchParams.get('searchTerm');

  const [isLoading, setIsLoading] = useState<boolean>(isEditMode);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [formData, setFormData] = useState<MarketItem>({
    contentId: undefined,
    companyId: parseInt(companyId || '0'),
    customerTypeId: parseInt(customerTypeId || '0'),
    itemNumber: '',
    marketItemNumber: '',
    erpInternalId: undefined,
    itemName: '',
    itemFriendlyName: '',
    marketItemName: '',
    primaryUom: '',
    orderableUom: '',
    primaryCategoryId: undefined,
    marketPrimaryCategoryId: undefined,
    primaryCategoryName: '',
    marketPrimaryCategoryName: '',
    packSize: undefined,
    caseSize: undefined,
    innerPackSize: undefined,
    weight: undefined,
    volume: undefined,
    minQuantity: undefined,
    minQuantityStartDate: '',
    minQuantityEndDate: '',
    maxQuantity: undefined,
    maxQuantityStartDate: '',
    maxQuantityEndDate: '',
    startDate: '',
    endDate: '',
    productType: '',
    parLevel: undefined,
    catalogueIds: [],
    catalogueEntries: [],
    orderTypeIds: [],
    orderTypeEntries: [],
    uoms: [],
    contentSuppliers: [],
  });

  const [instances, setInstances] = useState<Instance[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [catalogues, setCatalogues] = useState<MarketCatalogue[]>([]);
  const [orderTypes, setOrderTypes] = useState<MarketOrderType[]>([]);
  const [uomTypes, setUomTypes] = useState<UomType[]>([]);
  const [categories, setCategories] = useState<MarketCategory[]>([]);
  const [marketSuppliers, setMarketSuppliers] = useState<MarketSupplier[]>([]);
  const [useInventorySKU, setUseInventorySKU] = useState<boolean>(false);

  const isParEnabled = markets.find(m => m.customerTypeId === formData.customerTypeId)?.isParEnabled === true;

  useEffect(() => {
    loadInstances();
    loadUomTypes(companyId || '');
    if (isEditMode && id && companyId && customerTypeId) {
      loadProduct(id, companyId, customerTypeId);
    } else {
      setIsLoading(false);
    }
  }, [isEditMode, id, companyId, customerTypeId]);

  useEffect(() => {
    const loadCompanyConfig = async () => {
      if (!formData.companyId || formData.companyId === 0) return;
      try {
        const result = await client.getCompanyConfig(formData.companyId);
        if (result && result.data && result.data.useInventorySKU != null) {
          setUseInventorySKU(result.data.useInventorySKU);
        }
      } catch (error) {
        logError(error);
      }
    };
    loadCompanyConfig();
  }, [formData.companyId]);

  useEffect(() => {
    if (formData.companyId > 0) {
      loadMarkets(formData.companyId.toString());
      loadCatalogues(formData.companyId.toString(), formData.customerTypeId?.toString() || '');
      loadOrderTypes(formData.companyId.toString(), formData.customerTypeId?.toString() || '');
      loadCategories(formData.companyId.toString());
      loadMarketSuppliers(formData.companyId.toString(), formData.customerTypeId?.toString() || '');
    }
  }, [formData.companyId, formData.customerTypeId]);

  const loadInstances = async () => {
    try {
      const response = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setInstances(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load instances');
    }
  };

  const loadMarkets = async (instanceId: string) => {
    try {
      const response = await client.getMarkets({
        companyId: instanceId,
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setMarkets(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load markets');
    }
  };

  const loadCatalogues = async (instanceId: string, marketId: string) => {
    try {
      const response = await client.getMarketCatalogues({
        companyId: instanceId,
        customerTypeId: marketId,
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setCatalogues(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load catalogues');
    }
  };

  const loadUomTypes = async (cId: string) => {
    try {
      const response = await client.getUomTypes({
        companyId: cId || companyId || '',
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (response.success && response.data) {
        setUomTypes(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load UOM types');
    }
  };

  const loadCategories = async (instanceId: string) => {
    try {
      const response = await client.getMarketCategories({
        companyId: instanceId,
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setCategories(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load categories');
    }
  };

  const loadOrderTypes = async (instanceId: string, marketId: string) => {
    try {
      const response = await client.getMarketOrderTypes({
        companyId: instanceId,
        customerTypeId: marketId,
        pageNumber: 0,
        pageSize: 2147483647,
      });

      if (response.success && response.data) {
        setOrderTypes(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load order types');
    }
  };

  const loadMarketSuppliers = async (instanceId: string, marketId: string) => {
    try {
      const response = await client.getMarketSuppliers({
        companyId: instanceId,
        customerTypeId: marketId,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setMarketSuppliers(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load market suppliers');
    }
  };

  const loadProduct = async (productId: string, companyId: string, customerTypeId: string) => {
    try {
      const response = await client.getMarketItem(productId, companyId, customerTypeId);
      if (response.success && response.data) {
        setFormData(response.data);
      } else {
        setErrorMessage(response.message || t('loadErrorMessage'));
        setShowErrorMessage(true);
      }
    } catch (error) {
      logError('Failed to load product');
      setErrorMessage(t('loadErrorMessage'));
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof MarketItem) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleNumberInputChange = (field: keyof MarketItem) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setFormData(prev => ({
      ...prev,
      companyId: value?.id || 0,
      customerTypeId: 0,
      catalogueIds: [],
      catalogueEntries: [],
      orderTypeIds: [],
      orderTypeEntries: [],
    }));
  };

  const handleMarketChange = (event: React.SyntheticEvent, value: Market | null) => {
    setFormData(prev => ({
      ...prev,
      customerTypeId: value?.customerTypeId || 0,
      catalogueIds: [],
      catalogueEntries: [],
      orderTypeIds: [],
      orderTypeEntries: [],
    }));
  };

  const handleCategoryChange = (event: React.SyntheticEvent, value: MarketCategory | null) => {
    setFormData(prev => ({
      ...prev,
      marketPrimaryCategoryId: value?.categoryId || undefined,
      primaryCategoryId: value?.categoryId || undefined,
      marketPrimaryCategoryName: value?.name || '',
      primaryCategoryName: value?.name || '',
    }));
  };

  const handleCatalogueToggle = (catalogueId: number) => {
    setFormData(prev => {
      const isCurrentlySelected = prev.catalogueIds.includes(catalogueId);
      if (isCurrentlySelected) {
        return {
          ...prev,
          catalogueIds: prev.catalogueIds.filter(id => id !== catalogueId),
          catalogueEntries: prev.catalogueEntries.filter(entry => entry.catalogueId !== catalogueId),
        };
      } else {
        return {
          ...prev,
          catalogueIds: [...prev.catalogueIds, catalogueId],
          catalogueEntries: [
            ...prev.catalogueEntries,
            {
              catalogueId,
              startDate: '',
              endDate: '',
            },
          ],
        };
      }
    });
  };

  const handleCatalogueEntryDateChange = (catalogueId: number, field: 'startDate' | 'endDate', value: string) => {
    setFormData(prev => ({
      ...prev,
      catalogueEntries: prev.catalogueEntries.map(entry =>
        entry.catalogueId === catalogueId
          ? { ...entry, [field]: value }
          : entry,
      ),
    }));
  };

  const handleOrderTypeEntryDateChange = (orderTypeId: number, field: 'startDate' | 'endDate', value: string) => {
    setFormData(prev => {
      const existingEntry = prev.orderTypeEntries.find(entry => entry.orderTypeId === orderTypeId);

      if (existingEntry) {
        return {
          ...prev,
          orderTypeEntries: prev.orderTypeEntries.map(entry =>
            entry.orderTypeId === orderTypeId
              ? { ...entry, [field]: value }
              : entry,
          ),
        };
      } else {
        return {
          ...prev,
          orderTypeEntries: [
            ...prev.orderTypeEntries,
            {
              orderTypeId,
              startDate: field === 'startDate' ? value : '',
              endDate: field === 'endDate' ? value : '',
            },
          ],
        };
      }
    });
  };

  const handleAddUom = () => {
    const newUom: MarketItemUom = {
      id: 0,
      uomTypeId: 0,
      uomTypeName: '',
      uomTypeCode: '',
      isOrderableUom: false,
      isPrimaryUom: false,
      isMeasurement: false,
      uomName: '',
      numberOfBaseUnitsOrEaches: 1,
      weight: undefined,
      volume: undefined,
      price: undefined,
    };

    setFormData(prev => ({
      ...prev,
      uoms: [...prev.uoms, newUom],
    }));
  };

  const handleRemoveUom = (index: number) => {
    setFormData(prev => ({
      ...prev,
      uoms: prev.uoms.filter((_, idx) => idx !== index),
    }));
  };

  const handleUomChange = (index: number, field: keyof MarketItemUom, value: string | number | boolean | undefined) => {
    setFormData(prev => ({
      ...prev,
      uoms: prev.uoms.map((uom, idx) => {
        if (idx === index) {
          const updatedUom = { ...uom, [field]: value };
          if (field === 'uomTypeId') {
            const selectedUomType = uomTypes.find(ut => ut.uomTypeId === value);
            if (selectedUomType) {
              updatedUom.uomTypeName = selectedUomType.uomName;
              updatedUom.uomTypeCode = selectedUomType.uomCode;
              updatedUom.uomName = selectedUomType.uomName;
            }
          }
          if (field === 'isPrimaryUom' && value === true) {
            return updatedUom;
          }
          if (field === 'isOrderableUom' && value === true) {
            return updatedUom;
          }
          return updatedUom;
        } else {
          if (field === 'isPrimaryUom' && value === true) {
            return { ...uom, isPrimaryUom: false };
          }
          if (field === 'isOrderableUom' && value === true) {
            return { ...uom, isOrderableUom: false };
          }
          return uom;
        }
      }),
    }));
  };

  const handleCancel = () => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);
    const queryString = params.toString();
    navigate(`/products${queryString ? '?' + queryString : ''}`);
  };

  const validateForm = (): boolean => {
    if (!formData.itemNumber || !formData.itemName) {
      setErrorMessage(t('validationRequired'));
      setShowErrorMessage(true);
      return false;
    }

    const primaryUoms = formData.uoms.filter(u => u.isPrimaryUom);
    const orderableUoms = formData.uoms.filter(u => u.isOrderableUom);

    if (primaryUoms.length !== 1) {
      setErrorMessage(t('atLeastOnePrimaryUom'));
      setShowErrorMessage(true);
      return false;
    }

    if (orderableUoms.length !== 1) {
      setErrorMessage(t('atLeastOneOrderableUom'));
      setShowErrorMessage(true);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);

    try {

      const primaryUom = formData.uoms.find(u => u.isPrimaryUom);
      const orderableUom = formData.uoms.find(u => u.isOrderableUom);

      const updatedFormData = {
        ...formData,
        primaryUom: primaryUom?.uomName || '',
        orderableUom: orderableUom?.uomName || '',
        storeNumber: selectedStore?.storeNumber || '',
      };

      if (isEditMode && id) {
        const response = await client.updateMarketItem(id, updatedFormData);
        if (response.success) {
          setShowSuccessMessage(true);
        } else {
          setErrorMessage(response.message || t('errorMessage'));
          setShowErrorMessage(true);
        }
      } else {
        const response = await client.createMarketItem(updatedFormData);
        if (response.success) {
          setShowSuccessMessage(true);
          if (response.data) {
            const params = new URLSearchParams();
            if (companyId) params.set('companyId', companyId);
            if (customerTypeId) params.set('customerTypeId', customerTypeId);
            if (searchTerm) params.set('searchTerm', searchTerm);
            navigate(`/products/edit/${response.data}?${params.toString()}`);
          }
        } else if (response.message) {
          setErrorMessage(response.message);
          setShowErrorMessage(true);
        } else {
          setErrorMessage(t('errorMessage'));
          setShowErrorMessage(true);
        }
      }
    } catch (error) {
      const errorMsg = extractErrorMessage(error);
      logError('Failed to save product');
      setErrorMessage(errorMsg || t('errorMessage'));
      setShowErrorMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <PageArea>
        <div>{t('loading')}</div>
      </PageArea>
    );
  }
  
  return (
    <PageArea>
      <Box sx={{ mb: 4 }}>
        <h2 style={{ marginBottom: '16px', color: '#333' }}>
          {isEditMode ? t('editProduct') : t('createProduct')}
        </h2>
      </Box>

      <Grid container
        spacing={8}>
        {/* Basic Information */}
        <Grid item
          xs={12}>
          <Paper sx={{ p: 4 }}>
            <h3 style={{ marginBottom: '24px', color: '#333', fontSize: '1.25rem' }}>
              {t('basicInformation')}
            </h3>
            <Grid container
              spacing={4}>
              <Grid item
                xs={12}
                md={6}>
                <Autocomplete
                  options={instances}
                  getOptionLabel={(option) => option.name}
                  value={instances.find(i => i.id === formData.companyId) || null}
                  onChange={handleInstanceChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('instance')}
                      required
                    />
                  )}
                  disabled={isEditMode}
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <Autocomplete
                  options={markets}
                  getOptionLabel={(option) => option.name}
                  value={markets.find(m => m.customerTypeId === formData.customerTypeId) || null}
                  onChange={handleMarketChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('market')}
                      required
                    />
                  )}
                  disabled={isEditMode || !formData.companyId}
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('itemNumber')}
                  value={formData.itemNumber}
                  onChange={handleInputChange('itemNumber')}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={useInventorySKU ? t('inventorySKU') : t('marketItemNumber')}
                  value={formData.marketItemNumber}
                  onChange={handleInputChange('marketItemNumber')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('itemName')}
                  value={formData.itemName}
                  onChange={handleInputChange('itemName')}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('itemFriendlyName')}
                  value={formData.itemFriendlyName}
                  onChange={handleInputChange('itemFriendlyName')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <FormControl fullWidth>
                  <TextField
                    select
                    label={t('productType')}
                    value={formData.productType || ''}
                    onChange={handleInputChange('productType')}
                    fullWidth
                  >
                    <MenuItem value="">
                      <em>{t('selectProductType')}</em>
                    </MenuItem>
                    <MenuItem value="FOOD">FOOD</MenuItem>
                    <MenuItem value="PAPER">PAPER</MenuItem>
                    <MenuItem value="EXPENSE">EXPENSE</MenuItem>
                    <MenuItem value="OTHER">OTHER</MenuItem>
                  </TextField>
                </FormControl>
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('marketItemName')}
                  value={formData.marketItemName}
                  onChange={handleInputChange('marketItemName')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('erpInternalId')}
                  type="number"
                  value={formData.erpInternalId || ''}
                  onChange={handleNumberInputChange('erpInternalId')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <Autocomplete
                  options={categories}
                  getOptionLabel={(option) => option.name}
                  value={categories.find(c => c.categoryId === formData.marketPrimaryCategoryId) || null}
                  onChange={handleCategoryChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('primaryCategory')}
                    />
                  )}
                  disabled={!formData.companyId}
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('imageUrl')}
                  value={formData.imageUrl ?? ''}
                  onChange={handleInputChange('imageUrl')}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Product Details */}
        <Grid item
          xs={12}>
          <Paper sx={{ p: 4 }}>
            <h3 style={{ marginBottom: '24px', color: '#333', fontSize: '1.25rem' }}>
              {t('productDetails')}
            </h3>
            <Grid container
              spacing={4}>
              <Grid item
                xs={12}
                md={4}>
                <TextField
                  label={t('packSize')}
                  type="number"
                  value={formData.packSize || ''}
                  onChange={handleNumberInputChange('packSize')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={4}>
                <TextField
                  label={t('caseSize')}
                  type="number"
                  value={formData.caseSize || ''}
                  onChange={handleNumberInputChange('caseSize')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={4}>
                <TextField
                  label={t('innerPackSize')}
                  type="number"
                  value={formData.innerPackSize || ''}
                  onChange={handleNumberInputChange('innerPackSize')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('weight')}
                  type="number"
                  value={formData.weight || ''}
                  onChange={handleNumberInputChange('weight')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('volume')}
                  type="number"
                  value={formData.volume || ''}
                  onChange={handleNumberInputChange('volume')}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Quantity Limits */}
        <Grid item
          xs={12}>
          <Paper sx={{ p: 4 }}>
            <h3 style={{ marginBottom: '24px', color: '#333', fontSize: '1.25rem' }}>
              {t('quantityLimits')}
            </h3>
            <Grid container
              spacing={4}>
              <Grid item
                xs={12}
                md={4}>
                <TextField
                  label={t('minQuantity')}
                  type="number"
                  value={formData.minQuantity || ''}
                  onChange={handleNumberInputChange('minQuantity')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={4}>
                <DatePicker
                  label={t('minQuantityStartDate')}
                  value={toDateValue(formData.minQuantityStartDate)}
                  onChange={(newValue) => {
                    const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                    setFormData(prev => ({ ...prev, minQuantityStartDate: formatted }));
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item
                xs={12}
                md={4}>
                <DatePicker
                  label={t('minQuantityEndDate')}
                  value={toDateValue(formData.minQuantityEndDate)}
                  onChange={(newValue) => {
                    const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                    setFormData(prev => ({ ...prev, minQuantityEndDate: formatted }));
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item
                xs={12}
                md={4}>
                <TextField
                  label={t('maxQuantity')}
                  type="number"
                  value={formData.maxQuantity || ''}
                  onChange={handleNumberInputChange('maxQuantity')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={4}>
                <DatePicker
                  label={t('maxQuantityStartDate')}
                  value={toDateValue(formData.maxQuantityStartDate)}
                  onChange={(newValue) => {
                    const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                    setFormData(prev => ({ ...prev, maxQuantityStartDate: formatted }));
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item
                xs={12}
                md={4}>
                <DatePicker
                  label={t('maxQuantityEndDate')}
                  value={toDateValue(formData.maxQuantityEndDate)}
                  onChange={(newValue) => {
                    const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                    setFormData(prev => ({ ...prev, maxQuantityEndDate: formatted }));
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              {/* PAR Level — only visible when the selected market has PAR ordering enabled */}
              {isParEnabled && (
                <Grid item
                  xs={12}
                  md={4}>
                  <TextField
                    label={t('parLevel')}
                    type="number"
                    inputProps={{ min: 1 }}
                    value={formData.parLevel ?? ''}
                    onChange={handleNumberInputChange('parLevel')}
                    fullWidth
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Valid Period */}
        <Grid item
          xs={12}>
          <Paper sx={{ p: 4 }}>
            <h3 style={{ marginBottom: '24px', color: '#333', fontSize: '1.25rem' }}>
              {t('validityPeriod')}
            </h3>
            <Grid container
              spacing={4}>
              <Grid item
                xs={12}
                md={6}>
                <DatePicker
                  label={t('startDate')}
                  value={toDateValue(formData.startDate)}
                  onChange={(newValue) => {
                    const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                    setFormData(prev => ({ ...prev, startDate: formatted }));
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <DatePicker
                  label={t('endDate')}
                  value={toDateValue(formData.endDate)}
                  onChange={(newValue) => {
                    const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                    setFormData(prev => ({ ...prev, endDate: formatted }));
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Order Type & Catalogue Assignment */}
        <Grid item
          xs={12}>
          <Paper sx={{ p: 4 }}>
            <h3 style={{ marginBottom: '24px', color: '#333', fontSize: '1.25rem' }}>
              Order Type & Catalogue Assignment
            </h3>
            {orderTypes.map((orderType) => {
              const orderTypeCatalogues = catalogues.filter(c => c.orderTypeIds.includes(orderType.orderTypeId));
              const orderTypeEntry = formData.orderTypeEntries.find(entry => entry.orderTypeId === orderType.orderTypeId);

              return (
                <Accordion
                  key={orderType.orderTypeId}
                  defaultExpanded
                  sx={{ mb: 2 }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Box sx={{ flex: 1, ml: 5 }}>
                        <strong>{orderType.name}</strong>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, mr: 5 }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <DatePicker
                          label="Start Date"
                          value={toDateValue(orderTypeEntry?.startDate)}
                          onChange={(newValue) => {
                            const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                            handleOrderTypeEntryDateChange(orderType.orderTypeId, 'startDate', formatted);
                          }}
                          slotProps={{ textField: { size: 'small' } }}
                        />
                        <DatePicker
                          label="End Date"
                          value={toDateValue(orderTypeEntry?.endDate)}
                          onChange={(newValue) => {
                            const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                            handleOrderTypeEntryDateChange(orderType.orderTypeId, 'endDate', formatted);
                          }}
                          slotProps={{ textField: { size: 'small' } }}
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('catalogues')}</TableCell>
                            <TableCell align="center">Assigned</TableCell>
                            <TableCell align="center">Start Date</TableCell>
                            <TableCell align="center">End Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orderTypeCatalogues.map((catalogue) => {
                            const isSelected = formData.catalogueIds.includes(catalogue.customerGroupId);
                            const catalogueEntry = formData.catalogueEntries.find(
                              entry => entry.catalogueId === catalogue.customerGroupId,
                            );
                            return (
                              <TableRow key={catalogue.customerGroupId}>
                                <TableCell>{catalogue.name}</TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleCatalogueToggle(catalogue.customerGroupId)}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <DatePicker
                                    value={toDateValue(catalogueEntry?.startDate)}
                                    disabled={!isSelected}
                                    onChange={(newValue) => {
                                      const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                                      handleCatalogueEntryDateChange(
                                        catalogue.customerGroupId,
                                        'startDate',
                                        formatted,
                                      );
                                    }}
                                    slotProps={{ textField: { size: 'small' } }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <DatePicker
                                    value={toDateValue(catalogueEntry?.endDate)}
                                    disabled={!isSelected}
                                    onChange={(newValue) => {
                                      const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                                      handleCatalogueEntryDateChange(
                                        catalogue.customerGroupId,
                                        'endDate',
                                        formatted,
                                      );
                                    }}
                                    slotProps={{ textField: { size: 'small' } }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Paper>
        </Grid>

        {/* UOM Configuration */}
        <Grid item
          xs={12}>
          <Paper sx={{ p: 4 }}>
            <Box display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}>
              <h3 style={{ margin: 0, color: '#333', fontSize: '1.25rem' }}>
                {t('uomConfiguration')}
              </h3>
              <Button
                variant="secondary"
                size="lg"
                startIcon={<SvgIcon><Add fill='black' /></SvgIcon>}
                onClick={handleAddUom}
              >
                {t('addUom')}
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('uomType')}</TableCell>
                    <TableCell>{t('isPrimaryUom')}</TableCell>
                    <TableCell>{t('isOrderableUom')}</TableCell>
                    <TableCell>{t('isMeasurement')}</TableCell>
                    <TableCell>{t('numberOfBaseUnits')}</TableCell>
                    <TableCell>{t('weight')}</TableCell>
                    <TableCell>{t('volume')}</TableCell>
                    <TableCell>{t('price')}</TableCell>
                    <TableCell>{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.uoms.map((uom, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <FormControl fullWidth>
                          <Select
                            value={uom.uomTypeId}
                            onChange={(e) => handleUomChange(index, 'uomTypeId', e.target.value)}
                          >
                            {uomTypes.map((uomType) => (
                              <MenuItem key={uomType.uomTypeId}
                                value={uomType.uomTypeId}>
                                {uomType.uomName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={uom.isPrimaryUom}
                          onChange={(e) => handleUomChange(index, 'isPrimaryUom', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={uom.isOrderableUom}
                          onChange={(e) => handleUomChange(index, 'isOrderableUom', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={uom.isMeasurement}
                          onChange={(e) => handleUomChange(index, 'isMeasurement', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={uom.numberOfBaseUnitsOrEaches}
                          onChange={(e) => handleUomChange(index, 'numberOfBaseUnitsOrEaches', Number(e.target.value))}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={uom.weight || ''}
                          onChange={(e) => handleUomChange(index, 'weight', e.target.value ? Number(e.target.value) : undefined)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={uom.volume || ''}
                          onChange={(e) => handleUomChange(index, 'volume', e.target.value ? Number(e.target.value) : undefined)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={uom.price || ''}
                          onChange={(e) => handleUomChange(index, 'price', e.target.value ? Number(e.target.value) : undefined)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton aria-label={t('delete')}
                          color="error"
                          onClick={() => handleRemoveUom(index)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Supplier SKUs */}
        {marketSuppliers?.length > 0 && <Grid item 
          xs={12}>
          <Paper sx={{ p: 4 }}>
            <h3 style={{ marginBottom: '24px', color: '#333', fontSize: '1.25rem' }}>
              {t('supplierDetails')}
            </h3>
            <Grid container 
              spacing={2}>
              {marketSuppliers.map((s) => {
                const existing = (formData.contentSuppliers || []).find(acc => acc.supplierId == s.supplierId);
                return (
                  <Grid container 
                    item 
                    xs={12} 
                    spacing={2} 
                    alignItems="center" 
                    key={s.supplierNumber}>
                    <Grid item 
                      md={4} 
                      xs={12}>
                      <Box fontWeight={500}>
                        {s.supplierName} - {s.supplierNumber}
                      </Box>
                    </Grid>
                    <Grid item 
                      md={4} 
                      xs={12}>
                      <TextField
                        label={t('itemNumber')}
                        value={existing?.itemNumber ?? ''}
                        onChange={e => {
                          const value = e.target.value;
                          setFormData(prev => {
                            const current = prev.contentSuppliers || [];
                            const i = current.findIndex(a => a.supplierId == s.supplierId);
                            if (i >= 0) {
                              const updated = [...current];
                              updated[i] = { ...updated[i], itemNumber: value };
                              return { ...prev, contentSuppliers: updated };
                            }
                            return { ...prev, contentSuppliers: [...current, { id: 0, contentId: 0, supplierId: s.supplierId, itemNumber: value, price: '' }] };
                          });
                        }}
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    <Grid item 
                      md={4} 
                      xs={12}>
                      <TextField
                        label={t('price')}
                        type="number"
                        value={existing?.price ?? ''}
                        onChange={e => {
                          const value = e.target.value;
                          setFormData(prev => {
                            const current = prev.contentSuppliers || [];
                            const i = current.findIndex(a => a.supplierId == s.supplierId);
                            if (i >= 0) {
                              const updated = [...current];
                              updated[i] = { ...updated[i], price: value };
                              return { ...prev, contentSuppliers: updated };
                            }
                            return { ...prev, contentSuppliers: [...current, { id: 0, contentId: 0, supplierId: s.supplierId, itemNumber: '', price: value }] };
                          });
                        }}
                        size="small"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>
        }

        {/* Store Quantity Limits */}
        {isEditMode && formData.companyId > 0 && formData.customerTypeId && formData.customerTypeId > 0 && formData.itemNumber && (
          <Grid item
            xs={12}>
            <Paper sx={{ p: 4 }}>
              <StoreLevelMOQGrid
                companyId={formData.companyId}
                customerTypeId={formData.customerTypeId}
                itemNumber={formData.itemNumber}
              />
            </Paper>
          </Grid>
        )}

        {/* Action Buttons */}
        <Grid item
          xs={12}>
          <Box display="flex"
            justifyContent="flex-end"
            gap={3}
            pt={2}>
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? t('saving') : (isEditMode ? t('update') : t('create'))}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Success/Error Messages */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={5000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success"
          variant="filled">
          {t('successMessage')}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showErrorMessage}
        autoHideDuration={5000}
        onClose={() => setShowErrorMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error"
          variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default ProductForm;
